/**
 * Invoices and AR — `design/mockups/10-invoicing-payments-ar/invoicing-main.png`.
 *
 * The list is the same engine as Jobs and Contacts, which is most of the
 * screen. What is specific to money sits around it:
 *
 *   the AR strip, which is what is owed and how old it is;
 *   "Needs you today", which is the four questions somebody opens this screen
 *   to answer — what is waiting for approval, what has been earned and not
 *   billed, what failed to reach the customer, and what is being argued about;
 *   and Collections, which is the same open balances grouped by customer with
 *   a suggested next step, because chasing money is done per customer and not
 *   per invoice.
 *
 * Every figure comes from the server. §F.2 is the only definition of a
 * balance, and a second one computed in the client would eventually disagree
 * with the first — most likely the week after an ACH came back.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { ListScreen, type ListMetric } from '../components/list-screen.js'
import type { BulkAction } from '../components/bulk-menu.js'
import {
  Button, Card, EmptyState, StatusChip, Tag, humanize,
} from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { SettleBalance } from '../components/settle-balance.js'
import { QuickBooksMapping } from '../components/quickbooks-mapping.js'
import { PaymentSettings } from '../components/payment-settings.js'
import { RowTray } from '../components/row-tray.js'
import { IconMail, IconReceipt, IconSettings } from '../frame/icons.js'
import { invoiceMark } from '../components/landing-marks.js'
import type { MenuItem } from '../components/data-list.js'
import { useList, type ListRow } from '../lib/use-list.js'
import { openItems, copyItem } from '../lib/row-menu.js'
import { formatDollars, formatNumber, formatListDate } from '../lib/format.js'
import { request } from '../lib/api.js'

/** The screen reads these whether or not the Fields menu is showing them. */
const ALWAYS = [
  'status', 'balance', 'amount', 'paid', 'daysLate', 'ageBucket', 'inDispute', 'bounced',
  'paymentPending', 'reminderStep', 'autopay', 'recurring', 'dueDate', 'reference',
  // Read but not shown as columns: they are the second line under the
  // customer's name, which is where the mockup puts them.
  'jobTitle', 'jobReference',
  // Where an invoice row goes when it is opened, until there is an invoice
  // page of its own to go to; and whether it has been sent, which decides
  // whether the row offers Approve or Send again.
  'jobId', 'sentAt',
]

interface ArSummary {
  outstanding: string
  pastDue: string
  invoices: number
  buckets: Record<string, { invoices: number; customers: number; balance: string }>
  collectedThisMonth: string
  paymentsThisMonth: number
  avgDaysToPay: number | null
}

export interface Queue {
  awaitingApproval: {
    count: number
    totalCents: number
    invoices: { id: string; reference: string; customer: string | null; total: string }[]
  }
  readyToInvoice: {
    jobId: string; reference: string; title: string; property: string
    earnedCents: number; completedOn: string | null
  }[]
  exceptions: {
    id: string; kind: string; detail: string | null; invoiceId: string | null
    contactId: string | null; reference: string | null; customer: string | null
  }[]
  disputes: {
    id: string; reference: string; customer: string | null; total: string; days: number
  }[]
}

/**
 * "1 invoice", not "1 invoices".
 *
 * Small, and it is on this screen more than any other: every card here counts
 * something, and the one that most often reads `1` is the one somebody is
 * about to act on.
 */
const plural = (count: number, noun: string): string =>
  `${formatNumber(count)} ${noun}${count === 1 ? '' : 's'}`

/** The aging buckets, in the order the strip draws them. */
const BUCKETS = [
  { key: 'current', label: 'Current', urgent: false },
  { key: '1-30', label: '1–30', urgent: false },
  { key: '31-60', label: '31–60', urgent: false },
  { key: '61-90', label: '61–90', urgent: true },
  { key: '90+', label: '90+', urgent: true },
] as const

function cards(ar: ArSummary | null): ListMetric[] {
  if (!ar) return []

  return [
    {
      key: 'outstanding',
      label: 'Outstanding (AR)',
      value: formatDollars(Number(ar.outstanding)),
      sub: `${plural(ar.invoices, 'invoice')} · ${formatDollars(Number(ar.pastDue))} past due`,
      subUrgent: Number(ar.pastDue) > 0,
      filter: [{ field: 'balance', op: 'gt' as const, value: 0 }],
    },
    ...BUCKETS.map((bucket) => {
      const row = ar.buckets[bucket.key]
      return {
        key: bucket.key,
        label: bucket.label,
        value: formatDollars(Number(row?.balance ?? 0)),
        sub: plural(row?.invoices ?? 0, 'invoice'),
        subUrgent: bucket.urgent && Number(row?.balance ?? 0) > 0,
        filter: [{ field: 'ageBucket', op: 'eq' as const, value: bucket.key }],
      }
    }),
    {
      key: 'collected',
      label: 'Collected this month',
      value: formatDollars(Number(ar.collectedThisMonth)),
      sub: ar.avgDaysToPay === null
        ? plural(ar.paymentsThisMonth, 'payment')
        : `avg ${ar.avgDaysToPay} days to pay`,
    },
  ]
}

export function Invoices({ me }: { me: MeResponse }) {
  const list = useList('invoices', { alwaysFetch: ALWAYS })
  const navigate = useNavigate()

  const [ar, setAr] = useState<ArSummary | null>(null)
  const [queue, setQueue] = useState<Queue | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = me.permissions.invoices === 'edit' || me.permissions.invoices === 'full'
  /*
   * A credit, a write-off and the QuickBooks mapping are all `full`. §6.9 puts
   * a minimum role on giving up a balance, and the mapping decides which
   * account every document in the company lands on.
   */
  const canSettle = me.permissions.invoices === 'full'
  const [settling, setSettling] = useState<
    { id: string; reference: string; balance: number } | null>(null)
  const [mappingOpen, setMappingOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const refresh = useCallback(async () => {
    const [summary, needs] = await Promise.all([
      request<ArSummary>('/invoices/ar'),
      request<Queue>('/invoices/queue'),
    ])
    setAr(summary)
    setQueue(needs)
  }, [])

  useEffect(() => {
    refresh().catch((cause: unknown) => {
      setNotice(cause instanceof Error ? cause.message : 'Could not load AR.')
    })
  }, [refresh])

  async function approve(ids: string[]) {
    setBusy(true)
    try {
      const result = await request<{ sent: string[]; failed: { message: string }[]
                                     undeliverable: string[] }>(
        '/invoices/approve', { method: 'POST', body: { ids } })

      await refresh()
      list.reload()

      /*
       * Three outcomes, not two. An invoice can be issued and still not reach
       * anybody — a bill-to contact with a dead email — and saying "6 sent"
       * when one of them bounced is the kind of reassurance that costs a
       * month's cash flow.
       */
      const parts = [`${formatNumber(result.sent.length)} sent`]
      if (result.undeliverable.length > 0) {
        parts.push(`${formatNumber(result.undeliverable.length)} could not be delivered`)
      }
      if (result.failed.length > 0) {
        parts.push(`${formatNumber(result.failed.length)} failed: ${result.failed[0]!.message}`)
      }
      setNotice(parts.join(' · '))
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * The two actions under an expanded invoice: open the work it bills, and the
   * one thing this invoice is waiting for — approval when it has not been
   * issued, another send when it has.
   */
  function trayActions(row: ListRow) {
    const actions = []
    if (row.jobId) {
      actions.push({
        key: 'job', label: 'Open the job', primary: true,
        onSelect: () => navigate(`/jobs/${String(row.jobId)}`),
      })
    }
    if (canEdit && row.status === 'awaiting_approval') {
      actions.push({
        key: 'approve', label: 'Approve and send',
        onSelect: () => void approve([String(row.id)]),
      })
    } else if (canEdit && row.sentAt) {
      actions.push({
        key: 'resend', label: 'Send it again',
        onSelect: () => void resend(String(row.id)),
      })
    }
    return actions
  }

  /**
   * The row's ⋯ menu, led by what this invoice is waiting for — v2's
   * "the record's Next-action verb as the highlighted first action".
   */
  function rowMenu(row: ListRow): MenuItem[] {
    const items: MenuItem[] = row.jobId
      ? openItems(`/jobs/${String(row.jobId)}`, navigate).map((item) => ({
        ...item,
        label: item.label === 'Open' ? 'Open the job' : 'Open the job in a new tab',
      }))
      : []

    if (canEdit && row.status === 'awaiting_approval') {
      items.push({
        key: 'approve', label: 'Approve and send', tone: 'primary',
        separatorBefore: items.length > 0,
        onSelect: () => void approve([String(row.id)]),
      })
    } else if (canEdit && row.sentAt) {
      items.push({
        key: 'resend', label: 'Send it again',
        separatorBefore: items.length > 0,
        onSelect: () => void resend(String(row.id)),
      })
    }

    /*
     * Giving up the balance, on the row that has one. Two items rather than
     * one "settle" — the dialog still makes you choose, but a menu that says
     * both words is a menu that teaches the difference before it is needed.
     */
    if (canSettle && Number(row.balance ?? 0) > 0) {
      const target = {
        id: String(row.id),
        reference: String(row.reference ?? ''),
        balance: Number(row.balance ?? 0),
      }
      items.push({
        key: 'credit', label: 'Credit or write off…',
        separatorBefore: items.length > 0,
        onSelect: () => setSettling(target),
      })
    }

    items.push(...copyItem('copy-reference', 'Copy invoice number', row.reference, true))
    return items
  }

  async function resend(invoiceId: string) {
    setBusy(true)
    try {
      const result = await request<{ delivered: boolean }>(
        `/invoices/${invoiceId}/resend`, { method: 'POST', body: {} })
      await refresh()
      list.reload()
      setNotice(result.delivered
        ? 'Sent.'
        : 'Still nowhere to send it — the contact has no channel enabled for invoices.')
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ListScreen
        area="invoices"
        title="Invoices"
        list={list}
        metrics={cards(ar)}
        /* Which QuickBooks account each kind of money lands on (§F.4). It is
           set once and then left alone, so it is a button rather than a tab. */
        actions={
          <>
            {/*
              * Both carry a glyph, because on a phone this row's buttons are
              * squares and a button with only words cannot be one — it keeps
              * its full width and pushes whatever is beside it off the screen.
              */}
            <Button
              onClick={() => setSettingsOpen(true)}
              aria-label="Invoice settings"
              title="Invoice settings"
            >
              <IconSettings size={15} />
              Settings
            </Button>
            {canSettle && (
              <Button
                onClick={() => setMappingOpen(true)}
                aria-label="QuickBooks mapping"
                title="QuickBooks mapping"
              >
                <IconReceipt size={15} />
                QuickBooks
              </Button>
            )}
          </>
        }
        beforeList={queue && canEdit ? (
          <NeedsYouToday
            queue={queue}
            busy={busy}
            onApproveAll={() => approve(queue.awaitingApproval.invoices.map((row) => row.id))}
            onOpenApprovals={() => list.setFilter([
              { field: 'status', op: 'eq' as const, value: 'awaiting_approval' },
            ])}
            onResend={resend}
            onOpenContact={(contactId) => navigate(`/contacts/${contactId}`)}
            onOpenInvoice={(id) => navigate(`/invoices/${id}`)}
            onOpenJob={(id) => navigate(`/jobs/${id}`)}
          />
        ) : undefined}
        columnWidths={{
          reference: 96,
          status: 128,
          amount: 92,
          paid: 92,
          balance: 92,
          dueDate: 112,
        }}
        primaryColumn={{
          field: 'customerName',
          header: 'Customer · for',
          width: 220,
          render: (row) => ({
            primary: String(row.customerName ?? '—'),
            secondary: [row.jobTitle, row.jobReference].filter(Boolean).join(' · '),
          }),
        }}
        cell={{
          status: (row) => <InvoiceStatus row={row} />,
          dueDate: (row) => <Due row={row} />,
        }}
        bulk={canEdit ? { actions: INVOICE_BULK(approve) } : undefined}
        /*
         * A ring that fills as the invoice is paid (`docs/list-marks.md` §3).
         * It is not a louder copy of the Status chip: the chip says "partially
         * paid" and the ring says *how* partially, which is the number
         * somebody scanning an AR list is actually after. `amount` and `paid`
         * are both default columns, so the ring and the Balance column cannot
         * disagree.
         */
        mark={{ render: invoiceMark }}
        tray={(row) => (
          <RowTray area="invoices" id={row.id} actions={trayActions(row)} />
        )}
        rowMenu={rowMenu}
        /*
         * An invoice opens the job it bills.
         *
         * There is no invoice page yet — it lands with the customer portal —
         * and until there is, `/invoices/:id` matches nothing and bounces to
         * Home, which is what a row click used to do. The job is where this
         * invoice's money, close-out and billing settings already are.
         */
        onRowClick={(row) => { if (row.jobId) navigate(`/jobs/${String(row.jobId)}`) }}
        emptyTitle="No invoices yet"
        emptyBody="An invoice comes from a job's billing settings, or from the run at 6am. Nothing here means nothing is owed."
        importOrgUnitId={me.scope.selected[0]}
      />

      <SettleBalance
        open={settling !== null}
        onClose={() => setSettling(null)}
        invoice={settling}
        onDone={(message) => {
          setNotice(message)
          list.reload()
          void refresh()
        }}
      />

      <QuickBooksMapping open={mappingOpen} onClose={() => setMappingOpen(false)} />

      <PaymentSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        canEdit={canSettle}
      />

      {notice && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </>
  )
}

/**
 * The status as a person reads it.
 *
 * Disputed, Bounced and "payment processing" are flags on top of a status
 * (§F.3), not statuses, and they are what the row needs doing about — so they
 * win over the status underneath when there is only room for one chip.
 */
function InvoiceStatus({ row }: { row: ListRow }) {
  if (row.inDispute) return <Tag tone="bad">Disputed</Tag>
  if (row.bounced) return <Tag tone="bad">Bounced</Tag>
  if (row.paymentPending) return <Tag tone="info">Processing</Tag>

  const status = String(row.status ?? 'draft')
  const late = row.daysLate !== null && row.daysLate !== undefined
  if (late && ['sent', 'viewed', 'partially_paid'].includes(status)) {
    return <Tag tone="bad">Overdue</Tag>
  }

  return (
    <StatusChip group="invoice" status={status}>{humanize(status)}</StatusChip>
  )
}

/** The due cell: a date, or how late it is, which is the thing worth reading. */
function Due({ row }: { row: ListRow }) {
  const late = row.daysLate
  if (late !== null && late !== undefined) {
    return (
      <span className="tnum font-semibold text-danger">
        {formatNumber(Number(late))} days late
      </span>
    )
  }
  return <span className="tnum text-text">{formatListDate(String(row.dueDate ?? ''))}</span>
}

/**
 * The queue — §6.9's "Needs you today".
 *
 * Each card is a thing that will not fix itself, and each carries the one
 * button that fixes it. A card with nothing in it is not drawn: an empty queue
 * should look like an empty queue, not like four zeroes.
 */
export function NeedsYouToday({
  queue, busy, onApproveAll, onOpenApprovals, onResend, onOpenContact, onOpenInvoice,
  onOpenJob,
}: {
  queue: Queue
  busy: boolean
  onApproveAll: () => void
  onOpenApprovals: () => void
  onResend: (invoiceId: string) => void
  onOpenContact: (id: string) => void
  onOpenInvoice: (id: string) => void
  onOpenJob: (id: string) => void
}) {
  const count = (queue.awaitingApproval.count > 0 ? 1 : 0)
    + (queue.readyToInvoice.length > 0 ? 1 : 0)
    + queue.exceptions.length + queue.disputes.length

  if (count === 0) {
    return (
      <div className="px-4 pb-3">
        <EmptyState
          title="Nothing needs you today"
          body="Everything billable is billed, everything sent arrived, and nothing is being argued about."
        />
      </div>
    )
  }

  const earned = queue.readyToInvoice.reduce((sum, row) => sum + row.earnedCents, 0)
  const oldest = queue.readyToInvoice[0]

  return (
    <section className="px-4 pb-3">
      <h2 className="mb-2 flex items-center gap-2 text-[13px] font-bold text-text">
        Needs you today
        <span className="font-normal text-text-muted">{formatNumber(count)}</span>
      </h2>

      {/*
        * `min-w-0` on the cards, because a grid item will not shrink below
        * what is inside it: the two buttons in the first card are `shrink-0`,
        * so on a phone the card grew to fit them and hung 290px off the right
        * of the screen. The rows inside wrap instead — buttons under the
        * words when there is no room beside them.
        */}
      <div className="grid min-w-0 gap-2 md:grid-cols-2 [&>*]:min-w-0">
        {queue.awaitingApproval.count > 0 && (
          <Card tone="warn-edge">
            <div className="flex flex-wrap items-start justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-text">
                  {plural(queue.awaitingApproval.count, 'invoice')} waiting for approval
                </p>
                <p className="mt-0.5 truncate text-[11px] text-text-muted">
                  {formatDollars(queue.awaitingApproval.totalCents / 100)} ·{' '}
                  {queue.awaitingApproval.invoices.slice(0, 3)
                    .map((row) => row.customer).filter(Boolean).join(', ')}
                  {queue.awaitingApproval.invoices.length > 3
                    && `, +${queue.awaitingApproval.invoices.length - 3}`}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-1.5">
                <Button onClick={onOpenApprovals}>Open</Button>
                <Button variant="primary" disabled={busy} onClick={onApproveAll}>
                  Review &amp; approve all
                </Button>
              </div>
            </div>
          </Card>
        )}

        {queue.readyToInvoice.length > 0 && (
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-text">
                  {plural(queue.readyToInvoice.length, 'completed job')} not yet invoiced
                </p>
                <p className="mt-0.5 truncate text-[11px] text-text-muted">
                  {formatDollars(earned / 100)} earned
                  {oldest && ` · oldest ${oldest.property}`}
                </p>
              </div>
              {oldest && (
                <Button className="shrink-0" onClick={() => onOpenJob(oldest.jobId)}>
                  Open oldest
                </Button>
              )}
            </div>
          </Card>
        )}

        {queue.exceptions.map((exception) => (
          <Card key={exception.id} tone="bad-edge">
            <div className="flex flex-wrap items-start justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-text">
                  {exception.reference
                    ? `${exception.reference} could not be delivered`
                    : 'An invoice could not be delivered'}
                </p>
                <p className="mt-0.5 truncate text-[11px] text-text-muted">
                  {exception.customer ? `${exception.customer} · ` : ''}
                  {exception.detail ?? humanize(exception.kind)}
                </p>
              </div>
              {/*
                * §6.9's two halves, in the order they have to happen: the
                * email bounced because the contact is wrong, so resending
                * before somebody fixes it just bounces again.
                */}
              <div className="flex shrink-0 flex-wrap gap-1.5">
                {exception.contactId && (
                  <Button onClick={() => onOpenContact(exception.contactId!)}>
                    Fix contact
                  </Button>
                )}
                {exception.invoiceId && (
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() => onResend(exception.invoiceId!)}
                  >
                    Resend
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}

        {queue.disputes.map((dispute) => (
          <Card key={dispute.id} tone="bad-edge">
            <div className="flex flex-wrap items-start justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-text">
                  {dispute.reference} is disputed
                </p>
                <p className="mt-0.5 truncate text-[11px] text-text-muted">
                  {dispute.customer ?? '—'} · {formatDollars(Number(dispute.total))} ·{' '}
                  reminders are on hold
                </p>
              </div>
              <Button className="shrink-0" onClick={() => onOpenInvoice(dispute.id)}>
                Open
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </section>
  )
}

/**
 * What the Invoices list can do to a selection (v3 §3.3).
 *
 * Approve & send still goes through `/invoices/approve`, which has three
 * outcomes rather than two — sent, undeliverable, failed — and a toast that
 * knows the difference. §3.5 says to leave that endpoint where it is for now,
 * so this is a `local` action: the engine draws the menu and the page keeps
 * its own request and its own three-way sentence.
 *
 * It is in the **Send** group, not Change, because what it does that matters
 * is put a bill in front of a customer. Approving is the bit before it.
 */
const INVOICE_BULK = (approve: (ids: string[]) => Promise<void>): BulkAction[] => [
  {
    key: 'approve_send',
    group: 'send',
    label: (n) => `Approve & send ${formatNumber(n)}`,
    icon: IconMail,
    appliesTo: (row) => row.status === 'awaiting_approval',
    note: (ctx) => {
      const waiting = ctx.rows.filter((row) => row.status === 'awaiting_approval').length
      if (ctx.rows.length === 0 || waiting === ctx.rows.length) return undefined
      return waiting === 0
        ? 'None of these are awaiting approval.'
        : `Only the ${formatNumber(waiting)} awaiting approval.`
    },
    local: (ctx) => approve(ctx.rows
      .filter((row) => row.status === 'awaiting_approval')
      .map((row) => String(row.id))),
  },
]
